import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDashboardMetrics, TIME_PERIOD_OPTIONS } from '@/hooks/useDashboardMetrics';
import { formatCurrency } from '@/utils/formatting';
import type { TimePeriod } from '@/types/dashboard';
const DashboardSummary = () => {
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('30d');
  const { data: metrics, isLoading, error } = useDashboardMetrics(timePeriod);

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <p className="text-destructive">Failed to load dashboard metrics</p>
      </div>
    );
  }

  return <div className="container mx-auto p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-2">Overview of your organization's performance and metrics</p>
        </div>
        
        <Select value={timePeriod} onValueChange={(value) => setTimePeriod(value as TimePeriod)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select period" />
          </SelectTrigger>
          <SelectContent>
            {TIME_PERIOD_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {isLoading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Total Donations Card */}
          <Card>
            <CardHeader>
              <CardTitle>Total Donations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Count:</span>
                <span className="font-medium">{metrics?.donations.count || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Average:</span>
                <span className="font-medium">{formatCurrency(metrics?.donations.average || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Paid:</span>
                <span className="font-medium text-green-600">{formatCurrency(metrics?.donations.paid || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Pending:</span>
                <span className="font-medium text-yellow-600">{formatCurrency(metrics?.donations.pending || 0)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-sm font-semibold">Total:</span>
                <span className="font-semibold">{formatCurrency(metrics?.donations.total || 0)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Event Tables Card */}
          <Card>
            <CardHeader>
              <CardTitle>Event Tables</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Total Tables:</span>
                <span className="font-medium">{metrics?.eventTables.totalTables || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Sold:</span>
                <span className="font-medium">{metrics?.eventTables.soldTables || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Available:</span>
                <span className="font-medium">{metrics?.eventTables.availableTables || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Paid Revenue:</span>
                <span className="font-medium text-green-600">{formatCurrency(metrics?.eventTables.paidRevenue || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Pending Revenue:</span>
                <span className="font-medium text-yellow-600">{formatCurrency(metrics?.eventTables.pendingRevenue || 0)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-sm font-semibold">Total Revenue:</span>
                <span className="font-semibold">{formatCurrency(metrics?.eventTables.totalRevenue || 0)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Event Tickets Card */}
          <Card>
            <CardHeader>
              <CardTitle>Event Tickets</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Count:</span>
                <span className="font-medium">{metrics?.tickets.count || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Paid:</span>
                <span className="font-medium text-green-600">{formatCurrency(metrics?.tickets.paid || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Pending:</span>
                <span className="font-medium text-yellow-600">{formatCurrency(metrics?.tickets.pending || 0)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-sm font-semibold">Total:</span>
                <span className="font-semibold">{formatCurrency(metrics?.tickets.total || 0)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Online Auction Card */}
          <Card>
            <CardHeader>
              <CardTitle>Online Auction</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Total Items:</span>
                <span className="font-medium">{metrics?.auction.totalItems || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Total Bids:</span>
                <span className="font-medium">{metrics?.auction.totalBids || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Estimated Value:</span>
                <span className="font-medium">{formatCurrency(metrics?.auction.totalEstimatedValue || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Paid Purchases:</span>
                <span className="font-medium text-green-600">{formatCurrency(metrics?.auction.paidPurchases || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Pending:</span>
                <span className="font-medium text-yellow-600">{formatCurrency(metrics?.auction.pendingPurchases || 0)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Table Competition Card */}
          <Card>
            <CardHeader>
              <CardTitle>Table Competition</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Table Donations:</span>
                <span className="font-medium">{metrics?.tableCompetition.count || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Average:</span>
                <span className="font-medium">{formatCurrency(metrics?.tableCompetition.average || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Paid:</span>
                <span className="font-medium text-green-600">{formatCurrency(metrics?.tableCompetition.paid || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Pending:</span>
                <span className="font-medium text-yellow-600">{formatCurrency(metrics?.tableCompetition.pending || 0)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-sm font-semibold">Total:</span>
                <span className="font-semibold">{formatCurrency(metrics?.tableCompetition.total || 0)}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>;
};
export default DashboardSummary;